import { HttpErrorResponse, HttpStatusCode } from "@angular/common/http";

import { TServerErrors } from "@app/core/types/response.type";

/** Key under which the messages that belong to the whole form rather than to one field are kept. */
export const FORM_LEVEL_ERROR_KEY = "";

const VALIDATION_STATUSES: readonly number[] = [HttpStatusCode.BadRequest, HttpStatusCode.UnprocessableEntity];

/**
 * Field → messages of the API error body, or `null` when it carries none.
 * `fieldErrors` gives the field messages; whatever is left in the flat `errors` list is form-level.
 * A body whose `errors` is already such a dictionary is taken as is.
 */
export function readApiFieldErrors(error: HttpErrorResponse): TServerErrors | null {
  const body: unknown = error.error?.error;
  const errors: unknown = readProperty(body, "errors");

  if (!Array.isArray(errors)) {
    return readDictionary(errors);
  }

  return fromFlatList(errors.filter(isString), readDictionary(readProperty(body, "fieldErrors")));
}

/** Every message of the API error body as a flat list, regardless of the field it belongs to. */
export function readApiErrorMessages(error: HttpErrorResponse): string[] {
  const errors: unknown = readProperty(error.error?.error, "errors");

  if (Array.isArray(errors)) {
    return errors.filter(isString);
  }

  return Object.values(readDictionary(errors) ?? {})
    .flat()
    .filter(isString);
}

export function isValidationStatus(status: number): boolean {
  return VALIDATION_STATUSES.includes(status);
}

function fromFlatList(messages: string[], fieldErrors: TServerErrors | null): TServerErrors | null {
  const fieldMessages = new Set(Object.values(fieldErrors ?? {}).flat());
  const formLevel = messages.filter(message => !fieldMessages.has(message));
  const result: TServerErrors = { ...(fieldErrors ?? {}) };

  if (formLevel.length > 0) {
    result[FORM_LEVEL_ERROR_KEY] = formLevel;
  }

  return Object.keys(result).length > 0 ? result : null;
}

function readDictionary(value: unknown): TServerErrors | null {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as TServerErrors) : null;
}

function readProperty(source: unknown, key: string): unknown {
  return source && typeof source === "object" ? (source as Record<string, unknown>)[key] : undefined;
}

function isString(value: unknown): value is string {
  return typeof value === "string";
}
